import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, del, errorMessage, post } from "@/lib/api";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import { modeLabel, typeLabel, whenLong } from "@/utils/eventFormat";
import "./EventPages.css";

/**
 * One event (events spec). Shows mode, capacity, and targets. Then:
 * - Your RSVP: events.Respond, a membership that matches a target, modes
 *   rsvp and rsvp_and_attendance.
 * - Roster: everyone who matches a target. On rsvp events staff see who has
 *   answered (and who has not, when require_rsvp is set). With
 *   events.ManageAttendance on attendance / rsvp_and_attendance events it is
 *   the physical grid: insert places the first mark, update changes or clears
 *   it, the viewer's own row is never marked here, and a physical mark
 *   replaces that person's RSVP on the same row.
 */

type StoredEvent = {
  id: string;
  title: string;
  type: string;
  description: string | null;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  location: string | null;
  mode: string;
  require_rsvp: boolean;
  max_capacity: number | null;
  targets: Array<{ ugp_id?: string | null; group_id?: string | null; period_id?: string | null; role?: string | null }>;
};

type Person = { ugpId: string; name: string; detail: string };
type Mark = { uisUgpId: string; status: string | null };
type Capacity = { max: number | null; used: number; remaining: number | null; full: boolean };

type Detail = {
  event: StoredEvent;
  roster: Person[];
  marks: Mark[];
  capacity: Capacity;
  mine: string | null;
  matches: boolean;
};

const RSVP = [
  { value: "RSVP_YES", label: "Yes" },
  { value: "RSVP_NO", label: "No" },
  { value: "RSVP_MAYBE", label: "Maybe" },
];

const PHYSICAL = [
  { value: "PRESENT", label: "Present" },
  { value: "ABSENT", label: "Absent" },
  { value: "LATE", label: "Late" },
  { value: "EXCUSED", label: "Excused" },
];

const RSVP_TEXT: Record<string, string> = { RSVP_YES: "Yes", RSVP_NO: "No", RSVP_MAYBE: "Maybe" };
const PHYSICAL_TEXT: Record<string, string> = {
  PRESENT: "Present",
  ABSENT: "Absent",
  LATE: "Late",
  EXCUSED: "Excused",
};

function isPhysical(status: string | null | undefined) {
  return Boolean(status && status in PHYSICAL_TEXT);
}

function isRsvp(status: string | null | undefined) {
  return Boolean(status && status in RSVP_TEXT);
}

function roleText(role: string | null | undefined) {
  if (!role) return "";
  const lower = role.toLowerCase();
  return lower.endsWith("s") ? lower : `${lower}s`;
}

function targetText(target: StoredEvent["targets"][number]) {
  if (target.ugp_id) return target.role ? `One person (${target.role.toLowerCase()})` : "One person";
  const bits = ["A group"];
  if (target.role) bits.push(`${roleText(target.role)} only`);
  if (target.period_id) bits.push("one period");
  return bits.join(", ");
}

function capacityFrom(event: StoredEvent, marks: Map<string, string | null>, fallback: Capacity): Capacity {
  if (event.max_capacity == null) return { ...fallback, max: null, remaining: null, full: false };
  const positive = event.mode === "attendance" ? "PRESENT" : "RSVP_YES";
  let used = 0;
  for (const status of marks.values()) if (status === positive) used += 1;
  const max = Number(event.max_capacity);
  const remaining = Math.max(0, max - used);
  return { max, used, remaining, full: remaining <= 0 };
}

export default function EventDetail() {
  const { eventId = "" } = useParams();
  const navigate = useNavigate();
  const { pageAccess, session, loading: authLoading } = useAuth();
  const canOpen = Boolean(pageAccess?.events);
  const actions = session?.acuityLearn?.eventActions;
  const attendance = session?.acuityLearn?.attendanceActions;
  const canRespond = Boolean(session?.acuityLearn?.capabilities?.respondToEvents);
  const canManageAttendance = Boolean(session?.acuityLearn?.capabilities?.manageAttendance);
  const myId = session?.currentMembership?.id != null ? String(session.currentMembership.id) : "";

  const [detail, setDetail] = useState<Detail | null>(null);
  const [marks, setMarks] = useState<Map<string, string | null>>(new Map());
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [rsvpSaving, setRsvpSaving] = useState(false);

  const load = useCallback(async () => {
    setError("");
    const envelope = await api<{ success: boolean; data: Detail }>(`/api/events/${encodeURIComponent(eventId)}`);
    const data = envelope.data || null;
    setDetail(data);
    setMarks(new Map((data?.marks || []).map((mark) => [mark.uisUgpId, mark.status])));
  }, [eventId]);

  useEffect(() => {
    if (!canOpen || !eventId) return;
    void load().catch((err) => setError(errorMessage(err)));
  }, [canOpen, eventId, load]);

  const event = detail?.event;
  const roster = detail?.roster || [];
  const capacity = useMemo(() => {
    if (!event || !detail) return null;
    return marks.size || detail.marks.length ? capacityFrom(event, marks, detail.capacity) : detail.capacity;
  }, [event, detail, marks]);

  if (authLoading) return null;
  if (!canOpen) return <Navigate to="/" replace state={accessDeniedNavigateState("events")} />;

  const hasRsvp = Boolean(event && event.mode !== "attendance");
  const showRsvp = Boolean(event && canRespond && detail?.matches && hasRsvp);
  const showGrid = Boolean(event && canManageAttendance && event.mode !== "rsvp");
  const showRsvpRoster = Boolean(event && hasRsvp && !showGrid);
  const showRoster = showGrid || showRsvpRoster;

  async function respond(status: string) {
    setError("");
    setRsvpSaving(true);
    try {
      await post(`/api/events/${encodeURIComponent(eventId)}/rsvp`, { status });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRsvpSaving(false);
    }
  }

  async function mark(ugpId: string, value: string) {
    const prior = marks.get(ugpId) ?? null;
    const next = prior === value ? null : value;
    setError("");
    setWarning("");
    setSaving((current) => ({ ...current, [ugpId]: true }));
    setMarks((current) => new Map(current).set(ugpId, next));
    try {
      const envelope = await post<{ success: boolean; data: { status: string | null; warning?: string | null } }>(
        `/api/events/${encodeURIComponent(eventId)}/attendance`,
        { uisUgpId: ugpId, status: next },
      );
      setMarks((current) => new Map(current).set(ugpId, envelope.data?.status ?? next));
      if (envelope.data?.warning) setWarning(envelope.data.warning);
    } catch (err) {
      setMarks((current) => new Map(current).set(ugpId, prior));
      setError(errorMessage(err));
    } finally {
      setSaving((current) => {
        const copy = { ...current };
        delete copy[ugpId];
        return copy;
      });
    }
  }

  async function remove() {
    setError("");
    try {
      await del(`/api/events/${encodeURIComponent(eventId)}`);
      navigate("/events");
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  const statuses = roster.map((person) => marks.get(person.ugpId) ?? null);
  const count = (fn: (status: string | null) => boolean) => statuses.filter(fn).length;
  const mine = detail?.mine ?? null;

  let fullNote = "";
  if (event && capacity?.full) {
    if (event.mode === "attendance") {
      fullNote = "This event is full. New Present marks are refused; anyone already Present can still be changed.";
    } else if (event.mode === "rsvp") {
      fullNote = "This event is full. New RSVP yes answers are refused.";
    } else {
      fullNote = "The RSVP list is full. Attendance marks still save.";
    }
  }

  return (
    <div className="al-ev">
      <Link className="al-ev-back" to="/events">
        <ChevronLeft size={14} aria-hidden="true" />
        All events
      </Link>

      {error ? (
        <p className="al-ev-error" role="alert">
          {error}
        </p>
      ) : null}
      {!event && !error ? <p className="al-ev-note">Loading event…</p> : null}

      {event ? (
        <>
          <header className="al-ev-head al-ev-head--detail">
            <div>
              <h1>{event.title}</h1>
              <p className="al-ev-sub">
                {typeLabel(event.type)} · {whenLong(event)}
              </p>
            </div>
            <div className="al-ev-actions">
              {actions?.remove ? (
                <button type="button" className="al-ev-danger" onClick={() => setConfirmDelete(true)}>
                  Delete
                </button>
              ) : null}
              {actions?.edit ? (
                <Link className="al-ev-button" to={`/events/${encodeURIComponent(event.id)}/edit`}>
                  Edit event
                </Link>
              ) : null}
            </div>
          </header>

          {confirmDelete ? (
            <div className="al-ev-confirm" role="alertdialog" aria-label="Delete this event">
              <p>Delete this event? Its RSVPs and attendance marks are removed too.</p>
              <div className="al-ev-actions">
                <button type="button" className="al-ev-button" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </button>
                <button type="button" className="al-ev-button al-ev-button--danger" onClick={() => void remove()}>
                  Delete event
                </button>
              </div>
            </div>
          ) : null}

          <dl className="al-ev-facts">
            <div>
              <dt>Location</dt>
              <dd>{event.location || <span className="al-ev-faint">Not set</span>}</dd>
            </div>
            <div>
              <dt>Mode</dt>
              <dd>{modeLabel(event.mode)}</dd>
              {event.require_rsvp && hasRsvp ? <dd className="al-ev-muted">RSVP required</dd> : null}
            </div>
            <div>
              <dt>Capacity</dt>
              {capacity?.max == null ? (
                <dd>No limit</dd>
              ) : (
                <>
                  <dd className={capacity.full ? "al-ev-full" : ""}>
                    {capacity.full
                      ? `Full · ${capacity.max} of ${capacity.max}`
                      : `${capacity.remaining} of ${capacity.max} left`}
                  </dd>
                  <dd className="al-ev-muted">
                    {event.mode === "attendance" ? "Counts Present" : "Counts RSVP yes"}
                  </dd>
                </>
              )}
            </div>
            <div>
              <dt>For</dt>
              {event.targets?.length ? (
                event.targets.map((target, index) => (
                  <dd key={target.ugp_id || `${target.group_id}-${target.period_id}-${target.role}-${index}`}>
                    {targetText(target)}
                  </dd>
                ))
              ) : (
                <dd className="al-ev-faint">No one yet</dd>
              )}
              {roster.length ? (
                <dd className="al-ev-muted">
                  {roster.length} {roster.length === 1 ? "person" : "people"} on the roster
                </dd>
              ) : null}
            </div>
          </dl>

          {event.description ? <p className="al-ev-description">{event.description}</p> : null}

          {showRsvp ? (
            <section className="al-ev-section" aria-labelledby="ev-your-rsvp">
              <h2 id="ev-your-rsvp">Your RSVP</h2>
              {isPhysical(mine) ? (
                <p className="al-ev-note">
                  Attendance has been taken: you were marked {PHYSICAL_TEXT[mine as string]}.
                </p>
              ) : (
                <>
                  <div className="al-ev-rsvp" role="radiogroup" aria-label="Your RSVP">
                    {RSVP.map((option) => {
                      const blocked = option.value === "RSVP_YES" && capacity?.full && mine !== "RSVP_YES";
                      return (
                        <label key={option.value} className={blocked ? "is-off" : ""}>
                          <input
                            type="radio"
                            name="my-rsvp"
                            value={option.value}
                            checked={mine === option.value}
                            disabled={rsvpSaving || Boolean(blocked)}
                            onChange={() => void respond(option.value)}
                          />
                          {option.label}
                        </label>
                      );
                    })}
                  </div>
                  <p className="al-ev-note">
                    {rsvpSaving
                      ? "Saving…"
                      : mine
                        ? `You answered ${RSVP_TEXT[mine] || mine}.`
                        : event.require_rsvp
                          ? "An answer is required."
                          : "You have not answered yet."}
                    {capacity?.full && mine !== "RSVP_YES" ? " This event is full." : ""}
                  </p>
                </>
              )}
            </section>
          ) : null}

          {showRoster ? (
            <section className="al-ev-section" aria-labelledby="ev-roster">
              <div className="al-ev-section-head">
                <h2 id="ev-roster">Roster</h2>
                <p className="al-ev-note">
                  {showGrid
                    ? event.mode === "rsvp_and_attendance"
                      ? "Everyone this event is for. Marking attendance replaces that person's RSVP. Changes save as you click."
                      : "Everyone this event is for. Changes save as you click."
                    : "Everyone this event is for, and how they answered."}
                </p>
              </div>

              {roster.length ? (
                <div className="al-ev-counts">
                  {hasRsvp ? (
                    <div>
                      <span className="al-ev-counts-label">RSVP</span>
                      <dl>
                        <div>
                          <dt>Yes</dt>
                          <dd>{count((s) => s === "RSVP_YES")}</dd>
                        </div>
                        <div>
                          <dt>No</dt>
                          <dd>{count((s) => s === "RSVP_NO")}</dd>
                        </div>
                        <div>
                          <dt>Maybe</dt>
                          <dd>{count((s) => s === "RSVP_MAYBE")}</dd>
                        </div>
                        <div>
                          <dt>No response</dt>
                          <dd>{count((s) => s == null)}</dd>
                        </div>
                      </dl>
                    </div>
                  ) : null}
                  {showGrid ? (
                    <div>
                      <span className="al-ev-counts-label">Attendance</span>
                      <dl>
                        {PHYSICAL.map((option) => (
                          <div key={option.value}>
                            <dt>{option.label}</dt>
                            <dd>{count((s) => s === option.value)}</dd>
                          </div>
                        ))}
                        <div>
                          <dt>Not marked</dt>
                          <dd>{count((s) => !isPhysical(s))}</dd>
                        </div>
                      </dl>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {fullNote ? <p className="al-ev-warn">{fullNote}</p> : null}
              {warning ? <p className="al-ev-warn">{warning}</p> : null}

              {!roster.length ? (
                <p className="al-ev-empty">No one matches this event's targets yet.</p>
              ) : (
                <div className="al-ev-scroll">
                  <table className="al-ev-table al-ev-roster">
                    <caption className="al-ev-sr">Roster for {event.title}</caption>
                    <thead>
                      <tr>
                        <th scope="col" className="al-ev-col-person">
                          Person
                        </th>
                        {hasRsvp ? (
                          <th scope="col" className="al-ev-col-rsvp">
                            RSVP
                          </th>
                        ) : null}
                        {showGrid
                          ? PHYSICAL.map((option) => (
                              <th scope="col" key={option.value} className="al-ev-col-mark">
                                {option.label}
                              </th>
                            ))
                          : null}
                        {showGrid ? (
                          <th scope="col" className="al-ev-col-clear">
                            <span className="al-ev-sr">Clear mark</span>
                          </th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {roster.map((person) => {
                        const status = marks.get(person.ugpId) ?? null;
                        const physical = isPhysical(status);
                        const own = Boolean(myId) && person.ugpId === myId;
                        const allowed = physical ? Boolean(attendance?.update) : Boolean(attendance?.insert);
                        const busy = Boolean(saving[person.ugpId]);
                        const noResponse = status == null;
                        return (
                          <tr key={person.ugpId} className={showGrid && !physical && !own ? "is-open" : ""}>
                            <th scope="row" className="al-ev-col-person">
                              <span className="al-ev-name">
                                {person.name}
                                {own ? <span className="al-ev-muted"> (you)</span> : null}
                              </span>
                              {person.detail ? <span className="al-ev-muted">{person.detail}</span> : null}
                              {own && showGrid ? (
                                <span className="al-ev-muted">Your own row uses Your RSVP, not this grid.</span>
                              ) : null}
                            </th>
                            {hasRsvp ? (
                              <td className="al-ev-col-rsvp">
                                {isRsvp(status) ? (
                                  RSVP_TEXT[status as string]
                                ) : physical ? (
                                  <span className="al-ev-faint" title="Replaced by the attendance mark">
                                    —
                                  </span>
                                ) : noResponse ? (
                                  <span className={event.require_rsvp ? "al-ev-pending" : "al-ev-muted"}>
                                    No response
                                  </span>
                                ) : null}
                              </td>
                            ) : null}
                            {showGrid
                              ? PHYSICAL.map((option) => {
                                  const blockedByCapacity =
                                    option.value === "PRESENT" &&
                                    event.mode === "attendance" &&
                                    Boolean(capacity?.full) &&
                                    status !== "PRESENT";
                                  const disabled =
                                    own ||
                                    busy ||
                                    blockedByCapacity ||
                                    (status === option.value ? !attendance?.update : !allowed);
                                  return (
                                    <td key={option.value} className="al-ev-col-mark">
                                      <label className="al-ev-radio">
                                        <input
                                          type="radio"
                                          name={`ev-${person.ugpId}`}
                                          value={option.value}
                                          checked={status === option.value}
                                          disabled={disabled}
                                          aria-label={`${option.label}, ${person.name}`}
                                          onChange={() => undefined}
                                          onClick={() => {
                                            if (!disabled) void mark(person.ugpId, option.value);
                                          }}
                                        />
                                      </label>
                                    </td>
                                  );
                                })
                              : null}
                            {showGrid ? (
                              <td className="al-ev-col-clear">
                                {physical && attendance?.update && !own ? (
                                  <button
                                    type="button"
                                    disabled={busy}
                                    aria-label={`Clear mark, ${person.name}`}
                                    onClick={() => void mark(person.ugpId, status as string)}
                                  >
                                    Clear
                                  </button>
                                ) : null}
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {showGrid && attendance && (!attendance.insert || !attendance.update) ? (
                <p className="al-ev-note al-ev-foot">
                  {attendance.insert
                    ? "You can place first marks. Changing or clearing a mark needs someone with update."
                    : attendance.update
                      ? "You can change or clear marks. The first mark needs someone with insert."
                      : "You can view this roster but not mark it."}
                </p>
              ) : null}
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
